import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Eye } from 'lucide-react';

import { useSearchParams, Link } from 'react-router-dom';
import { usePendingApplications } from '../../features/customer-applications-admin/api';

import {
  useListCustomers,
  useGetCustomer,
  useApproveCustomer,
  useDownloadCustomerDoc,
  usePreviewCustomerDoc,
  useSetCustomerDocStatus,
  useUpdateCustomer,
  useArchiveCustomer,
  useUnarchiveCustomer,
  useSendCustomerOrderDetails,
  useUploadDocForCustomer,
  useUpdateCustomerRequirements,
  useUpdateCustomerPrepay,
  useInviteManualCustomer,
  useInviteCustomer,
  fetchAllCustomersForExport,
} from '../../features/customers/api';

import InviteCustomerModal from './InviteCustomerModal';

import { AppSidebar } from '../../components/AppSidebar/AppSidebar';

import { toast } from 'react-toastify';

import { type CustomerTier, useListCustomerTiers } from '../../features/customer-tiers/api';

import CreateCustomerModal from './CreateCustomerModal';

import CustomerAddressesModal from './CustomerAddressesModal';

import { AuditLogModal } from '../../components/audit/AuditLogModal';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog/PdfPreviewDialog';

import CustomerSubusersModal from './CustomerSubusersModal';

import { Table } from '../../components/Table/Table';
import { CustomersTableScroll } from './CustomersTableScroll';

import SearchInput from '../../components/SearchInput/SearchInput';

import { useSortableColumns } from '../../hooks/sorting/useSortableColumns';

import { NavLink } from 'react-router-dom';

import { downloadAdminCustomersCsv } from '../../utils/exportCsv';

import './customers.scss';

import './customer-tier-assignment.scss';
import './customer-document-preview.scss';

interface CustomerDoc {
  id: string;

  type: 'AGREEMENT' | 'RESALE_CERTIFICATE' | 'CREDIT_APPLICATION';

  status: 'SUBMITTED' | 'REJECTED';

  uploadedAt: string;

  version: number;
}

interface Customer {
  id: string;

  name: string;

  email: string;

  notificationEmail?: string | null;

  status: string;

  updatedAt: string;

  documents: CustomerDoc[];

  /** State of the customer's headquarters (falls back to their primary address). */

  hqState: string | null;

  tierId?: string | null;

  blumeCreditAvailable?: number | null;
  blumeCreditLimit?: number | null;

  blumeOverdueInvoiceBalance?: number | null;

  blumeCreditUpdatedAt?: string | null;

  blumeCreditStaleAt?: string | null;

  /** The DeFeo-approved credit limit (cents) + when it was last pushed to Blume. */
  creditLimitCents?: number | null;

  blumeCreditLimitPushedAt?: string | null;

  /** Fallback for customers approved before creditLimitCents was stored. */
  application?: {
    decisionCreditLimitCents?: number | null;
    decisionTierId?: string | null;
    reviewedAt?: string | null;
  } | null;
}

function useDebounced(value: string, ms = 300) {
  const [v, setV] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setV(value), ms);

    return () => clearTimeout(timer);
  }, [value, ms]);

  return v;
}

function tierTextTone(hexColor: string): 'dark' | 'light' {
  const normalized = String(hexColor || '').replace('#', '');

  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) {
    return 'light';
  }

  const r = parseInt(normalized.slice(0, 2), 16);

  const g = parseInt(normalized.slice(2, 4), 16);

  const b = parseInt(normalized.slice(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.62 ? 'dark' : 'light';
}

function formatTierDiscount(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',

    currency: 'USD',
  }).format(Number(cents || 0) / 100);
}

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',

    currency: 'USD',

    maximumFractionDigits: 0,
  }).format(value);
}

function CustomerTierBadge({ tier }: { tier: CustomerTier }) {
  const tone = tierTextTone(tier.badgeColor);

  return (
    <span
      className="customer-tier-badge"
      title={`${tier.name} — ${formatTierDiscount(tier.discountAmountCents)} discount`}
    >
      <svg
        className="customer-tier-badge__background"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <rect width="100" height="32" rx="16" fill={tier.badgeColor} />
      </svg>

      <span className={`customer-tier-badge__text customer-tier-badge__text--${tone}`}>
        {tier.name}
      </span>
    </span>
  );
}

export default function CustomersPage() {
  const [sp, setSp] = useSearchParams();

  const [q, setQ] = useState(sp.get('q') || '');

  const debouncedQ = useDebounced(q, 350);

  const [status, setStatus] = useState(sp.get('status') || '');

  const [hasFrozenLocations, setHasFrozenLocations] = useState(
    sp.get('hasFrozenLocations') === 'true'
  );

  const [page, setPage] = useState(Number(sp.get('page') || 1));

  const [pageSize, setPageSize] = useState(Number(sp.get('pageSize') || 100));

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [showInvite, setShowInvite] = useState(false);

  const [showCreate, setShowCreate] = useState(false);

  const [addressesCustomerId, setAddressesCustomerId] = useState<string | null>(null);

  const [auditOpen, setAuditOpen] = useState(false);

  const [managingSubuserId, setManagingSubuserId] = useState<string | null>(null);

  const [isExportingCsv, setIsExportingCsv] = useState(false);

  const { sortBy, sortDirection, handleSort } = useSortableColumns({
    defaultSortBy: 'updated',

    defaultDirection: 'desc',

    onSortChange: () => setPage(1),
  });

  // Preserve sortBy/sortDir (owned by useSortableColumns) while syncing

  // this page's filter and pagination state to the URL.

  useEffect(() => {
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (debouncedQ) {
          next.set('q', debouncedQ);
        } else {
          next.delete('q');
        }

        if (status) {
          next.set('status', status);
        } else {
          next.delete('status');
        }

        if (hasFrozenLocations) {
          next.set('hasFrozenLocations', 'true');
        } else {
          next.delete('hasFrozenLocations');
        }

        next.set('page', String(page));

        next.set('pageSize', String(pageSize));

        return next;
      },

      { replace: true }
    );
  }, [debouncedQ, status, hasFrozenLocations, page, pageSize, setSp]);

  // Deep link from the orders table (/customers?customerId=<id>): open that

  // customer's record directly. Consume the param once so closing the modal

  // doesn't reopen it and a refresh doesn't re-trigger it.

  const deepLinkCustomerId = sp.get('customerId');

  useEffect(() => {
    if (!deepLinkCustomerId) return;

    setSelectedId(deepLinkCustomerId);

    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);

        next.delete('customerId');

        return next;
      },

      { replace: true }
    );
  }, [deepLinkCustomerId, setSp]);

  const { data, isLoading, isFetching } = useListCustomers({
    search: debouncedQ,

    status,

    hasFrozenLocations: hasFrozenLocations || undefined,

    page,

    pageSize,

    sortBy: sortBy || undefined,

    sortDir: sortDirection,
  });

  const customers = data?.customers ?? [];

  const totalPages = data?.totalPages ?? 1;

  const totalCount = data?.totalCount ?? 0;

  const { data: tiersData } = useListCustomerTiers();

  const customerTiers = tiersData?.tiers ?? [];

  const columns = [
    {
      id: 'name',

      description: "Customer's company name. Click to open their record.",

      header: 'Name',

      sortable: true,

      sortKey: 'name',

      render: (customer: Customer) => (
        <button
          type="button"
          className="btn btn--link"
          onClick={() => setSelectedId(customer.id)}
          style={{
            padding: 0,

            border: 'none',

            background: 'none',
          }}
          title="Manage Customer"
        >
          {customer.name || customer.email}
        </button>
      ),
    },

    {
      id: 'email',

      description: "Login email for the customer's primary contact.",

      header: 'Email',

      sortable: true,

      sortKey: 'email',

      render: (customer: Customer) => customer.email,
    },

    {
      id: 'hq-state',

      description:
        "State of the customer's headquarters. Sub-locations in other states don't affect this.",

      header: 'State',

      sortable: true,

      sortKey: 'hqState',

      render: (customer: Customer) =>
        customer.hqState ? customer.hqState : <span className="muted">—</span>,
    },

    {
      id: 'tier',

      description: 'Customer pricing tier assigned by the organization.',

      header: 'Tier',

      render: (customer: Customer) => {
        const tier = customer.tierId
          ? (customerTiers.find((candidate) => candidate.id === customer.tierId) ?? null)
          : null;

        return tier ? <CustomerTierBadge tier={tier} /> : <span className="muted">—</span>;
      },
    },

    {
      id: 'credit-limit',
      description: 'Credit limit from Blume. Stale values are last-known amounts.',
      header: 'Credit Limit',
      sortable: true,
      sortKey: 'creditLimit',
      render: (customer: Customer) => {
        if (customer.blumeCreditLimit == null) return <span className="muted">—</span>;
        return (
          <strong
            title={
              customer.blumeCreditStaleAt
                ? 'Last known Blume credit limit (stale)'
                : 'Credit limit synchronized from Blume'
            }
            style={{ color: customer.blumeCreditStaleAt ? '#7c8492' : '#172033' }}
          >
            {formatCurrency(customer.blumeCreditLimit)}
          </strong>
        );
      },
    },

    {
      id: 'available-credit',

      description:
        'Available customer credit from Blume. Stale values are the last known amount and may no longer be current.',

      header: 'Available Credit',

      sortable: true,

      sortKey: 'availableCredit',

      render: (customer: Customer) => {
        if (customer.blumeCreditAvailable === null || customer.blumeCreditAvailable === undefined) {
          return <span className="muted">—</span>;
        }

        const isStale = Boolean(customer.blumeCreditStaleAt);

        return (
          <div
            style={{
              display: 'flex',

              alignItems: 'center',

              gap: 8,

              whiteSpace: 'nowrap',
            }}
          >
            <strong
              style={{
                color: isStale ? '#7c8492' : '#172033',
              }}
            >
              {formatCurrency(customer.blumeCreditAvailable)}
            </strong>

            <span
              style={{
                display: 'inline-flex',

                alignItems: 'center',

                borderRadius: 999,

                padding: '2px 7px',

                fontSize: 10,

                fontWeight: 700,

                lineHeight: 1.4,

                background: isStale ? '#fff7e8' : '#edf8f1',

                color: isStale ? '#a86100' : '#247448',
              }}
              title={
                isStale
                  ? 'Last known Blume credit. This value is currently stale.'
                  : 'Current credit synchronized from Blume.'
              }
            >
              {isStale ? 'Stale' : 'Blume'}
            </span>
          </div>
        );
      },
    },

    {
      id: 'overdue-balance',

      description:
        'Customer overdue invoice balance from Blume. Stale values are the last known amount and may no longer be current.',

      header: 'Overdue Balance',

      sortable: true,

      sortKey: 'overdueBalance',

      render: (customer: Customer) => {
        if (
          customer.blumeOverdueInvoiceBalance === null ||
          customer.blumeOverdueInvoiceBalance === undefined
        ) {
          return <span className="muted">—</span>;
        }

        const isStale = Boolean(customer.blumeCreditStaleAt);

        return (
          <div
            style={{
              display: 'flex',

              alignItems: 'center',

              gap: 8,

              whiteSpace: 'nowrap',
            }}
          >
            <strong
              style={{
                color: isStale ? '#7c8492' : '#172033',
              }}
            >
              {formatCurrency(customer.blumeOverdueInvoiceBalance)}
            </strong>

            <span
              style={{
                display: 'inline-flex',

                alignItems: 'center',

                borderRadius: 999,

                padding: '2px 7px',

                fontSize: 10,

                fontWeight: 700,

                lineHeight: 1.4,

                background: isStale ? '#fff7e8' : '#edf8f1',

                color: isStale ? '#a86100' : '#247448',
              }}
              title={
                isStale
                  ? 'Last known Blume overdue balance. This value is currently stale.'
                  : 'Current overdue invoice balance synchronized from Blume.'
              }
            >
              {isStale ? 'Stale' : 'Blume'}
            </span>
          </div>
        );
      },
    },

    {
      id: 'status',

      description:
        'Where the customer is in onboarding: invited, under review, approved, rejected, or archived.',

      header: 'Status',

      sortable: true,

      sortKey: 'status',

      render: (customer: Customer) => (
        <span
          className="badge"
          style={{
            background:
              customer.status === 'APPROVED'
                ? '#dcfce7'
                : customer.status === 'INVITED'
                  ? '#eff6ff'
                  : customer.status === 'UNDER_REVIEW'
                    ? '#fef9c3'
                    : customer.status === 'REJECTED'
                      ? '#fee2e2'
                      : '#f3f4f6',

            border: '1px solid #e5e7eb',
          }}
        >
          {customer.status.replace('_', ' ')}
        </span>
      ),
    },

    {
      id: 'resale-cert',

      description: 'Whether a resale certificate is on file, and whether it was accepted.',

      header: 'Resale Cert',

      render: (customer: Customer) => {
        const document = (customer.documents ?? []).find(
          (item) => item.type === 'RESALE_CERTIFICATE'
        );

        if (!document) {
          return <span className="doc-status doc-status--no">No</span>;
        }

        if (document.status === 'REJECTED') {
          return <span className="doc-status doc-status--rejected">Rejected</span>;
        }

        return <span className="doc-status doc-status--yes">Yes</span>;
      },
    },

    {
      id: 'credit-app',

      description:
        'Whether a signed credit application / agreement is on file, and whether it was accepted.',

      header: 'Credit App',

      render: (customer: Customer) => {
        const document = (customer.documents ?? []).find((item) => item.type === 'AGREEMENT');

        if (!document) {
          return <span className="doc-status doc-status--no">No</span>;
        }

        if (document.status === 'REJECTED') {
          return <span className="doc-status doc-status--rejected">Rejected</span>;
        }

        return <span className="doc-status doc-status--yes">Yes</span>;
      },
    },

    {
      id: 'updated',

      description: 'When this customer record was last changed.',

      header: 'Updated',

      sortable: true,

      sortKey: 'updated',

      render: (customer: Customer) => new Date(customer.updatedAt).toLocaleString(),
    },

    {
      id: 'actions',

      description:
        'Open the customer, place an order for them, manage their locations, or manage their subusers.',

      header: 'Actions',
      resizable: false,
      maxWidth: 250,
      minWidth: 250,
      defaultWidth: 250,

      className: 'ta-right',

      render: (customer: Customer) => (
        <div
          className="actions-inner"
          style={{
            gap: 8,

            justifyContent: 'flex-end',
          }}
        >
          <button className="btn btn--ghost" onClick={() => setAddressesCustomerId(customer.id)}>
            Locations
          </button>

          <button className="btn btn--ghost" onClick={() => setManagingSubuserId(customer.id)}>
            Subusers
          </button>
        </div>
      ),
    },
  ];

  const handleExportCsv = async () => {
    if (isExportingCsv || totalCount === 0) {
      return;
    }

    setIsExportingCsv(true);

    try {
      const allCustomers = await fetchAllCustomersForExport({
        search: debouncedQ,
        status,
        hasFrozenLocations: hasFrozenLocations || undefined,
        sortBy: sortBy || undefined,
        sortDir: sortDirection,
      });

      const exportedCount = downloadAdminCustomersCsv(allCustomers, columns, customerTiers);

      toast.success(
        `Downloaded ${exportedCount.toLocaleString()} ${
          exportedCount === 1 ? 'customer' : 'customers'
        } to CSV.`
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || error?.message || 'Failed to download customers to CSV.'
      );
    } finally {
      setIsExportingCsv(false);
    }
  };

  return (
    <div className="layout">
      <AppSidebar />

      <div className="card">
        <div className="flex-between" style={{ marginBottom: 12 }}>
          <div className="customer_title">
            <h2 className="card__title">Customers</h2>

            <p className="card__subtitle">
              Showing {customers.length} of {totalCount} total
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleExportCsv}
              disabled={totalCount === 0 || isExportingCsv}
              title="Export every customer matching the current filters"
            >
              {isExportingCsv ? 'Exporting…' : `Export All (${totalCount.toLocaleString()})`}
            </button>

            <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
              Create Customer
            </button>

            <button className="btn btn--primary" onClick={() => setShowInvite(true)}>
              Invite Customer
            </button>

            <button className="btn btn--primary" onClick={() => setAuditOpen(true)}>
              Audit log
            </button>
          </div>
        </div>

        <PendingApplicationsBanner />

        <div className="card card--bleed-top customers-filters">
          <div className="grid customers-filters__grid">
            <div className="form-control">
              <label>Search</label>

              <SearchInput
                placeholder="Name or Email…"
                value={q}
                onValueChange={(value) => {
                  setQ(value);

                  setPage(1);
                }}
              />
            </div>

            <div className="form-control">
              <label>Status</label>

              <select
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);

                  setPage(1);
                }}
              >
                <option value="">All</option>

                <option value="INVITED">Invited</option>

                <option value="UNDER_REVIEW">Under review</option>

                <option value="APPROVED">Approved</option>

                <option value="REJECTED">Rejected</option>

                <option value="ARCHIVED">Archived</option>
              </select>
            </div>

            <div className="form-control">
              <label>Page size</label>

              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));

                  setPage(1);
                }}
              >
                {[100, 200, 300].map((number) => (
                  <option key={number} value={number}>
                    {number}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="form-control"
              style={{
                display: 'flex',

                justifyContent: 'flex-end',
              }}
            >
              <label
                style={{
                  display: 'flex',

                  alignItems: 'center',

                  gap: 8,

                  minHeight: 40,

                  cursor: 'pointer',

                  margin: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={hasFrozenLocations}
                  onChange={(event) => {
                    setHasFrozenLocations(event.target.checked);

                    setPage(1);
                  }}
                  style={{
                    width: 16,

                    height: 16,

                    margin: 0,
                  }}
                />

                <span>Customers with frozen locations</span>
              </label>
            </div>
          </div>
        </div>

        <CustomersTableScroll>
          <Table
            columns={columns}
            data={customers}
            isLoading={isLoading}
            emptyMessage="No customers match your filters."
            className="customers-table"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        </CustomersTableScroll>

        {selectedId && (
          <CustomerDrawer
            customerId={selectedId}
            tiers={customerTiers}
            onClose={() => setSelectedId(null)}
            onManageLocations={() => {
              setSelectedId(null);

              setAddressesCustomerId(selectedId);
            }}
          />
        )}

        <div className="pager-row">
          <div />

          <div className="pager">
            <button
              className="btn btn--ghost"
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>

            <span>
              Page {page} / {totalPages}
            </span>

            <button
              className="btn btn--ghost"
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>

        {isFetching && (
          <div className="muted" style={{ marginTop: 8 }}>
            Refreshing…
          </div>
        )}

        {showInvite && (
          <InviteCustomerModal
            onClose={() => setShowInvite(false)}
            onInvited={() => {
              setShowInvite(false);
            }}
          />
        )}

        {showCreate && (
          <CreateCustomerModal
            onClose={() => setShowCreate(false)}
            onInvited={() => {
              setShowCreate(false);
            }}
          />
        )}

        {addressesCustomerId && (
          <CustomerAddressesModal
            customerId={addressesCustomerId}
            onClose={() => setAddressesCustomerId(null)}
            onBack={() => {
              setSelectedId(addressesCustomerId);

              setAddressesCustomerId(null);
            }}
          />
        )}

        {managingSubuserId && (
          <CustomerSubusersModal
            customerId={managingSubuserId}
            onClose={() => setManagingSubuserId(null)}
          />
        )}

        <AuditLogModal open={auditOpen} onClose={() => setAuditOpen(false)} entityType="CUSTOMER" />
      </div>
    </div>
  );
}

function CustomerDrawer({
  customerId,

  tiers,

  onClose,

  onManageLocations,
}: {
  customerId: string;

  tiers: CustomerTier[];

  onClose: () => void;

  onManageLocations: () => void;
}) {
  const { data, isLoading } = useGetCustomer(customerId);

  const approve = useApproveCustomer();

  const update = useUpdateCustomer();

  const archive = useArchiveCustomer();

  const unarchive = useUnarchiveCustomer();

  const downloadDoc = useDownloadCustomerDoc();

  // Preview fetches the same protected PDF as Download, but returns a Blob
  // instead of initiating a browser download.
  const previewDoc = usePreviewCustomerDoc();
  const [documentPreview, setDocumentPreview] = useState<{
    url: string;
    docId: string;
    filename: string;
    title: string;
  } | null>(null);
  const previewRequestId = useRef(0);

  // Prevent an in-flight preview request opening a dialog after this customer
  // changes or the drawer unmounts. PdfPreviewDialog owns URL revocation.
  useEffect(() => {
    return () => {
      previewRequestId.current += 1;
    };
  }, [customerId]);

  const closeDocumentPreview = () => {
    previewRequestId.current += 1;
    setDocumentPreview(null);
  };

  const onPreviewDocument = async (doc: CustomerDoc, filename: string, title: string) => {
    const requestId = ++previewRequestId.current;
    try {
      const result: unknown = await previewDoc.mutateAsync({ docId: doc.id });

      // Support a Blob or an Axios response containing one. Reject unexpected
      // responses rather than creating an empty/invalid PDF iframe URL.
      const blob =
        result instanceof Blob
          ? result
          : result &&
              typeof result === 'object' &&
              'data' in result &&
              (result as { data?: unknown }).data instanceof Blob
            ? (result as { data: Blob }).data
            : null;

      if (!blob) throw new Error('Preview API did not return a PDF file.');
      if (requestId !== previewRequestId.current) return;

      // Some document endpoints serve application/octet-stream even for PDFs.
      // Force PDF content type so the browser can render the blob in an iframe.
      const pdfBlob =
        blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setDocumentPreview({ url, docId: doc.id, filename, title });
    } catch (error: any) {
      if (requestId !== previewRequestId.current) return;
      console.error('Customer document preview failed:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Unable to preview PDF.');
    }
  };

  const setStatus = useSetCustomerDocStatus(customerId);

  const [requiresPrepay, setRequiresPrepay] = useState(false);

  const updatePrepay = useUpdateCustomerPrepay();

  const inviteManual = useInviteManualCustomer(customerId);

  const invite = useInviteCustomer();

  const sendOrderDetails = useSendCustomerOrderDetails();

  const [manualInviteEmail, setManualInviteEmail] = useState('');

  const [showReinviteDialog, setShowReinviteDialog] = useState(false);

  const [reinviteEmail, setReinviteEmail] = useState('');

  const normalizedInviteEmail = manualInviteEmail.trim().toLowerCase();

  const isEmailValid = /^\S+@\S+\.\S+$/.test(normalizedInviteEmail);

  const onInviteManual = () => {
    if (!isEmailValid) {
      toast.error('Enter a valid email first.');

      return;
    }

    inviteManual.mutate(
      {
        email: normalizedInviteEmail,
      },

      {
        onSuccess: () => {
          toast.success('Invite sent.');

          setManualInviteEmail('');
        },

        onError: (error: any) => toast.error(error?.response?.data?.error || 'Invite failed'),
      }
    );
  };

  const customer = data?.customer;

  const [tierId, setTierId] = useState('');

  const [applyTierToAllLocations, setApplyTierToAllLocations] = useState(false);

  useEffect(() => {
    setTierId(customer?.tierId ?? '');

    setApplyTierToAllLocations(false);
  }, [customerId, customer?.tierId]);

  const selectedTier = tierId ? (tiers.find((tier) => tier.id === tierId) ?? null) : null;

  // Pricing region + per-customer visibility. `''` means unset — auto-suggested

  // from the customer's primary address state below, admin-overridable.

  // All pricing config — region, delivered prices, and the "show pricing to

  // customer" visibility toggle — now lives on each sub-location (the Addresses

  // modal). Nothing pricing-related remains on this master customer modal.

  const reinvite = async () => {
    const nextEmail = reinviteEmail.trim();

    const currentEmail = customer?.email?.trim() || '';

    if (!nextEmail) {
      toast.warn('Email is required.');

      return;
    }

    try {
      const response = await invite.mutateAsync({
        email: currentEmail,

        subEmail: nextEmail.toLowerCase() !== currentEmail.toLowerCase() ? nextEmail : undefined,

        isAgreementRequered: customer?.isAgreementRequered,

        isResaleCertificateRequered: customer?.isResaleCertificateRequered,

        name: customer?.name,
      });

      toast.success('Invite sent.');

      if (response?.inviteUrl) {
        await navigator.clipboard?.writeText(response.inviteUrl).catch(() => {});

        toast.info('Invite link copied to clipboard.');
      }

      setShowReinviteDialog(false);
    } catch (error: unknown) {
      const message =
        (
          error as {
            response?: {
              data?: {
                error?: string;
              };
            };
          }
        )?.response?.data?.error || 'Failed to send invite.';

      toast.error(message);
    }
  };

  const missing = data?.missing ?? [];

  useEffect(() => {
    if (!customer) {
      return;
    }

    setRequiresPrepay(Boolean(customer.requiresPrepay));
  }, [customer]);

  const canApproveBase =
    Boolean(customer) &&
    customer?.status !== 'APPROVED' &&
    customer?.status !== 'ARCHIVED' &&
    customer?.status !== 'INVITED' &&
    missing.length === 0;

  const canApprove = canApproveBase;

  const isApproved = customer?.status === 'APPROVED' || customer?.status === 'INVITED';

  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    setCustomerName(customer?.name ?? '');
  }, [customer?.name]);

  const [notificationEmail, setNotificationEmail] = useState('');

  const usesNotificationEmail = customer?.status === 'MANUAL' || customer?.status === 'INVITED';

  useEffect(() => {
    setNotificationEmail(customer?.notificationEmail ?? '');
  }, [customer?.notificationEmail]);

  const normalizedNotificationEmail = notificationEmail.trim().toLowerCase();

  const notificationEmailValid =
    !notificationEmail.trim() || /^\S+@\S+\.\S+$/.test(normalizedNotificationEmail);

  const [customerHeadquarters, setCustomerHeadquarters] = useState('');

  useEffect(() => {
    setCustomerHeadquarters(
      customer?.addresses?.length > 0
        ? `${customer.addresses[0].line1}, ${customer.addresses[0].city}, ${customer.addresses[0].state}, ${customer.addresses[0].country}, ${customer.addresses[0].postalCode}`
        : ''
    );
  }, [customer?.addresses]);

  const canEditCustomerDetails = isApproved || customer?.status === 'MANUAL';

  const nameChanged =
    canEditCustomerDetails &&
    Boolean(customerName.trim()) &&
    customerName.trim() !== customer?.name;

  const notificationEmailChanged =
    usesNotificationEmail &&
    normalizedNotificationEmail !==
      String(customer?.notificationEmail ?? '')
        .trim()

        .toLowerCase();

  const tierChanged = tierId !== (customer?.tierId ?? '');

  const applyTierEverywhere = Boolean(tierId) && applyTierToAllLocations;

  const hasCustomerChanges =
    Boolean(nameChanged) ||
    Boolean(notificationEmailChanged) ||
    Boolean(tierChanged) ||
    applyTierEverywhere;

  const onPrimary = () => {
    if (!customer) {
      return;
    }

    if (usesNotificationEmail && !notificationEmailValid) {
      toast.warn('Enter a valid notification email.');

      return;
    }

    const payload = {
      customerId,

      locationId: customer.locationId ?? null,

      name: customerName.trim(),

      notificationEmail: usesNotificationEmail ? normalizedNotificationEmail || null : undefined,

      tierId: tierId || null,

      applyTierToAllLocations: applyTierEverywhere,
    };

    if (canEditCustomerDetails) {
      if (!hasCustomerChanges) {
        return;
      }

      update.mutate(payload, {
        onSuccess: () => {
          setApplyTierToAllLocations(false);

          toast.success(
            applyTierEverywhere
              ? 'Customer updated and tier discount enabled for all locations.'
              : 'Customer updated.'
          );
        },

        onError: (error: any) => toast.error(error?.response?.data?.error || 'Update failed'),
      });

      return;
    }

    if (!canApprove) {
      return;
    }

    approve.mutate(payload, {
      onSuccess: () => {
        setApplyTierToAllLocations(false);

        toast.success(
          applyTierEverywhere
            ? 'Customer approved and tier discount enabled for all locations.'
            : 'Customer approved.'
        );
      },

      onError: (error: any) => toast.error(error?.response?.data?.error || 'Approve failed'),
    });
  };

  const onArchive = () => {
    if (!customer) {
      return;
    }

    archive.mutate(customerId, {
      onSuccess: () => {
        toast.success('Customer archived.');

        onClose();
      },

      onError: (error: any) => toast.error(error?.response?.data?.error || 'Archive failed'),
    });
  };

  const onSendOrderDetails = async () => {
    if (!customer) {
      return;
    }

    const ok = window.confirm(
      `Email ${customer.name || 'this customer'} a summary of their open orders?`
    );

    if (!ok) {
      return;
    }

    try {
      const res = await sendOrderDetails.mutateAsync(customerId);

      if (res.sent) {
        toast.success(
          `Sent ${res.openOrderCount} open order${res.openOrderCount === 1 ? '' : 's'}.`
        );
      } else {
        toast.info('This customer has no open orders to send.');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to send order details.');
    }
  };

  const onUnarchive = () => {
    if (!customer) {
      return;
    }

    unarchive.mutate(customerId, {
      onSuccess: () => toast.success('Customer unarchived.'),

      onError: (error: any) => toast.error(error?.response?.data?.error || 'Unarchive failed'),
    });
  };

  const onTogglePrepay = async (next: boolean) => {
    if (!customer) {
      return;
    }

    const previousValue = requiresPrepay;

    setRequiresPrepay(next);

    try {
      await updatePrepay.mutateAsync({
        customerId,

        requiresPrepay: next,
      });

      toast.success('Updated prepay requirement.');
    } catch (error: any) {
      setRequiresPrepay(previousValue);

      toast.error(error?.response?.data?.error || 'Failed to update prepay requirement.');
    }
  };

  const chipStyle = (background: string, color: string) => ({
    background,

    color,

    border: '1px solid #e5e7eb',

    padding: '2px 8px',

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 600,
  });

  const badge = (documentStatus: string | null | undefined) => {
    const map: Record<
      string,
      {
        bg: string;

        color: string;

        label: string;
      }
    > = {
      REJECTED: {
        bg: '#fee2e2',

        color: '#991b1b',

        label: 'Rejected',
      },

      SUBMITTED: {
        bg: '#fef9c3',

        color: '#854d0e',

        label: 'Submitted',
      },
    };

    if (!documentStatus) {
      return <span>-</span>;
    }

    const style = map[documentStatus] || {
      bg: '#e5e7eb',

      color: '#374151',

      label: documentStatus,
    };

    return <span style={chipStyle(style.bg, style.color)}>{style.label}</span>;
  };

  const [isAgreementRequired, setIsAgreementRequired] = useState(false);

  const [isResaleCertificateRequired, setIsResaleCertificateRequired] = useState(false);

  const updateRequirements = useUpdateCustomerRequirements();

  useEffect(() => {
    if (!customer) {
      return;
    }

    setIsAgreementRequired(Boolean(customer.isAgreementRequered));

    setIsResaleCertificateRequired(Boolean(customer.isResaleCertificateRequered));
  }, [customer]);

  const onToggleAgreementRequired = async (next: boolean) => {
    if (!customer) {
      return;
    }

    const previousValue = isAgreementRequired;

    setIsAgreementRequired(next);

    try {
      await updateRequirements.mutateAsync({
        customerId,

        isAgreementRequered: next,
      });

      toast.success('Updated requirement.');
    } catch (error: any) {
      setIsAgreementRequired(previousValue);

      toast.error(error?.response?.data?.error || 'Failed to update requirement.');
    }
  };

  const onToggleResaleRequired = async (next: boolean) => {
    if (!customer) {
      return;
    }

    const previousValue = isResaleCertificateRequired;

    setIsResaleCertificateRequired(next);

    try {
      await updateRequirements.mutateAsync({
        customerId,

        isResaleCertificateRequered: next,
      });

      toast.success('Updated requirement.');
    } catch (error: any) {
      setIsResaleCertificateRequired(previousValue);

      toast.error(error?.response?.data?.error || 'Failed to update requirement.');
    }
  };

  const uploadDoc = useUploadDocForCustomer(customerId);

  const handleUploadDoc =
    (type: 'AGREEMENT' | 'RESALE_CERTIFICATE') =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;

      if (!file) {
        return;
      }

      try {
        await uploadDoc.mutateAsync({
          type,

          file,
        });

        toast.success('Uploaded.');
      } catch (error: any) {
        toast.error(error?.response?.data?.error || 'Upload failed.');
      }
    };

  if (isLoading || !customer) {
    return (
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal__backdrop" onClick={onClose} />

        <div className="modal__panel" style={{ width: '100%' }}>
          <div className="modal__header">
            <h3 className="modal__title">Customer</h3>

            <button className="btn btn--ghost" onClick={onClose}>
              Close
            </button>
          </div>

          <div className="modal__body">
            <div className="skeleton" style={{ height: 240 }} />
          </div>
        </div>
      </div>
    );
  }

  const customerWithBlume = customer as typeof customer & {
    blumeCreditAvailable?: number | null;
    blumeCreditLimit?: number | null;

    blumeOverdueInvoiceBalance?: number | null;

    blumeCreditUpdatedAt?: string | null;

    blumeCreditStaleAt?: string | null;
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />

      <div className="modal__panel" style={{ width: '100%' }}>
        <div className="modal__header">
          <h3 className="modal__title">Customer</h3>

          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          <div
            className="grid"
            style={{
              gridTemplateColumns: '1fr 1fr',

              gap: 12,
            }}
          >
            <div>
              <strong>Name:</strong> {customer.name}
            </div>

            <div>
              <strong>Email:</strong> {customer.email}
            </div>

            <div>
              <strong>Status:</strong> {customer.status}
            </div>

            {(() => {
              const limitCents =
                customer.creditLimitCents ?? customer.application?.decisionCreditLimitCents ?? null;
              if (limitCents == null) return null;
              return (
                <div>
                  <strong>Approved credit limit:</strong> {formatCurrency(limitCents / 100)}
                  {/* Only confirm a successful Blume push — a "pending" chip would
                      read as perpetually stuck for orgs that don't use Blume. */}
                  {customer.blumeCreditLimitPushedAt && (
                    <span className="muted tiny" style={{ marginLeft: 8 }}>
                      · synced to Blume
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          <div
            style={{
              marginTop: 16,

              padding: '14px 16px',

              border: customerWithBlume.blumeCreditStaleAt
                ? '1px solid #f3dfb5'
                : '1px solid #dce9e1',

              background: customerWithBlume.blumeCreditStaleAt ? '#fffaf0' : '#f6fbf8',

              borderRadius: 10,

              display: 'flex',

              alignItems: 'center',

              justifyContent: 'space-between',

              gap: 24,

              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'grid',

                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',

                gap: 28,

                flex: 1,
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#667085' }}>Credit Limit</div>
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 22,
                    lineHeight: 1.2,
                    fontWeight: 700,
                    color: customerWithBlume.blumeCreditStaleAt ? '#7c8492' : '#172033',
                  }}
                >
                  {formatCurrency(customerWithBlume.blumeCreditLimit)}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 12,

                    fontWeight: 600,

                    color: '#667085',
                  }}
                >
                  Available Credit
                </div>

                <div
                  style={{
                    marginTop: 3,

                    fontSize: 22,

                    lineHeight: 1.2,

                    fontWeight: 700,

                    color: customerWithBlume.blumeCreditStaleAt ? '#7c8492' : '#172033',
                  }}
                >
                  {formatCurrency(customerWithBlume.blumeCreditAvailable)}
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: 12,

                    fontWeight: 600,

                    color: '#667085',
                  }}
                >
                  Overdue Balance
                </div>

                <div
                  style={{
                    marginTop: 3,

                    fontSize: 22,

                    lineHeight: 1.2,

                    fontWeight: 700,

                    color: customerWithBlume.blumeCreditStaleAt ? '#7c8492' : '#172033',
                  }}
                >
                  {formatCurrency(customerWithBlume.blumeOverdueInvoiceBalance)}
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',

                alignItems: 'flex-end',

                flexDirection: 'column',

                gap: 5,
              }}
            >
              {customerWithBlume.blumeCreditAvailable == null &&
              customerWithBlume.blumeCreditLimit == null &&
              customerWithBlume.blumeOverdueInvoiceBalance == null ? (
                <span className="muted">No Blume financial data available</span>
              ) : customerWithBlume.blumeCreditStaleAt ? (
                <>
                  <span
                    style={{
                      display: 'inline-flex',

                      alignItems: 'center',

                      borderRadius: 999,

                      padding: '2px 7px',

                      fontSize: 10,

                      fontWeight: 700,

                      lineHeight: 1.4,

                      background: '#fff7e8',

                      color: '#a86100',
                    }}
                  >
                    Stale
                  </span>

                  <span className="muted tiny">
                    Last confirmed{' '}
                    {customerWithBlume.blumeCreditUpdatedAt
                      ? new Date(customerWithBlume.blumeCreditUpdatedAt).toLocaleString()
                      : 'previously'}
                  </span>
                </>
              ) : (
                <>
                  <span
                    style={{
                      display: 'inline-flex',

                      alignItems: 'center',

                      borderRadius: 999,

                      padding: '2px 7px',

                      fontSize: 10,

                      fontWeight: 700,

                      lineHeight: 1.4,

                      background: '#edf8f1',

                      color: '#247448',
                    }}
                  >
                    Blume
                  </span>

                  {customerWithBlume.blumeCreditUpdatedAt && (
                    <span className="muted tiny">
                      Updated {new Date(customerWithBlume.blumeCreditUpdatedAt).toLocaleString()}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          <h4 style={{ marginTop: 16 }}>Requirements</h4>

          <div
            className="grid"
            style={{
              gridTemplateColumns: '1fr 1fr',

              gap: 12,
            }}
          >
            <label
              style={{
                display: 'flex',

                gap: 10,

                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={isAgreementRequired}
                onChange={(event) => onToggleAgreementRequired(event.target.checked)}
                disabled={update.isPending || approve.isPending}
              />

              <span>
                <strong>Agreement required</strong>
              </span>
            </label>

            <label
              style={{
                display: 'flex',

                gap: 10,

                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={isResaleCertificateRequired}
                onChange={(event) => onToggleResaleRequired(event.target.checked)}
                disabled={update.isPending || approve.isPending}
              />

              <span>
                <strong>Resale cert required</strong>
              </span>
            </label>

            <label
              style={{
                display: 'flex',

                gap: 10,

                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                checked={requiresPrepay}
                onChange={(event) => onTogglePrepay(event.target.checked)}
                disabled={updatePrepay.isPending}
              />

              <span>
                <strong>Requires prepay</strong>
              </span>
            </label>
          </div>

          {usesNotificationEmail && (
            <>
              <h4 style={{ marginTop: 16 }}>Invite</h4>

              <div className="card" style={{ padding: 12 }}>
                <div
                  style={{
                    display: 'grid',

                    gridTemplateColumns: '1fr auto',

                    gap: 10,

                    alignItems: 'end',
                  }}
                >
                  <div className="form-control" style={{ marginBottom: 0 }}>
                    <label>Email</label>

                    <input
                      value={manualInviteEmail}
                      onChange={(event) => setManualInviteEmail(event.target.value)}
                      placeholder="customer@company.com"
                    />

                    {!manualInviteEmail.trim() ? null : !isEmailValid ? (
                      <div className="muted tiny">Enter a valid email address.</div>
                    ) : null}
                  </div>

                  <button
                    className="btn btn--primary"
                    onClick={onInviteManual}
                    disabled={!isEmailValid || inviteManual.isPending}
                    title={!isEmailValid ? 'Enter a valid email first' : ''}
                  >
                    {inviteManual.isPending ? 'Inviting…' : 'Invite'}
                  </button>
                </div>
              </div>
            </>
          )}

          <h4 style={{ marginTop: 16 }}>Details</h4>

          <div
            className="grid"
            style={{
              gridTemplateColumns: '1fr 1fr',

              gap: 12,
            }}
          >
            <div className="form-control">
              <label>Name</label>

              <input
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                placeholder="Customer name"
              />
            </div>

            {usesNotificationEmail && (
              <div className="form-control">
                <label>Notification Email</label>

                <input
                  type="email"
                  value={notificationEmail}
                  onChange={(event) => setNotificationEmail(event.target.value)}
                  placeholder="notifications@company.com"
                  autoComplete="email"
                  aria-invalid={!notificationEmailValid}
                />

                {!notificationEmail.trim() ? (
                  <div className="muted tiny" style={{ marginTop: 4 }}>
                    Used for order and delivery notifications for this customer.
                  </div>
                ) : !notificationEmailValid ? (
                  <div className="error-text" style={{ marginTop: 4 }}>
                    Enter a valid email address.
                  </div>
                ) : (
                  <div className="muted tiny" style={{ marginTop: 4 }}>
                    Notifications will be sent to {normalizedNotificationEmail}.
                  </div>
                )}
              </div>
            )}

            <div className="form-control">
              <label>Customer Tier</label>

              <select
                value={tierId}
                onChange={(event) => {
                  const nextTierId = event.target.value;

                  setTierId(nextTierId);

                  if (!nextTierId) {
                    setApplyTierToAllLocations(false);
                  }
                }}
              >
                <option value="">No tier</option>

                {tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name} — {formatTierDiscount(tier.discountAmountCents)} discount
                  </option>
                ))}
              </select>

              {selectedTier ? (
                <>
                  <div className="customer-tier-assignment__preview">
                    <CustomerTierBadge tier={selectedTier} />

                    <span className="muted tiny">
                      {formatTierDiscount(selectedTier.discountAmountCents)} discount
                    </span>
                  </div>

                  <label
                    style={{
                      display: 'flex',

                      alignItems: 'flex-start',

                      gap: 10,

                      marginTop: 10,

                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={applyTierToAllLocations}
                      onChange={(event) => setApplyTierToAllLocations(event.target.checked)}
                      style={{
                        width: 16,

                        height: 16,

                        marginTop: 2,
                      }}
                    />

                    <span>
                      <strong>Apply to all locations</strong>

                      <span
                        className="muted tiny"
                        style={{
                          display: 'block',

                          marginTop: 2,
                        }}
                      >
                        Enable this customer's tier discount at every existing delivery location.
                      </span>
                    </span>
                  </label>
                </>
              ) : tiers.length === 0 ? (
                <div className="muted tiny customer-tier-assignment__hint">
                  No tiers configured yet. Add them under Organization → Customer Tiers.
                </div>
              ) : (
                <div className="muted tiny customer-tier-assignment__hint">
                  This customer does not have a pricing tier assigned.
                </div>
              )}
            </div>

            {customerHeadquarters && (
              <div className="form-control">
                <label>Headquarters</label>

                <input value={customerHeadquarters} placeholder="Headquarters location" disabled />
              </div>
            )}
          </div>

          <h4 style={{ marginTop: 16 }}>Documents</h4>

          <table className="table">
            <thead>
              <tr>
                <th>Type</th>

                <th>Status</th>

                <th>Version</th>

                <th>Uploaded</th>

                <th>PDF</th>

                <th className="ta-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {['AGREEMENT', 'RESALE_CERTIFICATE'].map((type) => {
                const document = (customer.documents || []).find(
                  (item: CustomerDoc) => item.type === type
                );

                const niceName = type === 'AGREEMENT' ? 'agreement.pdf' : 'resale-certificate.pdf';

                const isRejected = document?.status === 'REJECTED';

                return (
                  <tr key={type}>
                    <td>{type === 'AGREEMENT' ? 'Agreement' : 'Resale Certificate'}</td>

                    <td>{badge(document?.status)}</td>

                    <td>
                      {document ? <span className="version-badge">v{document.version}</span> : '—'}
                    </td>

                    <td>{document ? new Date(document.uploadedAt).toLocaleString() : '—'}</td>

                    <td>
                      {document ? (
                        <div className="customer-document-pdf-actions">
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() =>
                              onPreviewDocument(
                                document,
                                niceName,
                                type === 'AGREEMENT' ? 'Agreement' : 'Resale Certificate'
                              )
                            }
                            disabled={previewDoc.isPending}
                            title={`Preview ${niceName}`}
                          >
                            <Eye size={14} />
                            {previewDoc.isPending && previewDoc.variables?.docId === document.id
                              ? 'Opening…'
                              : 'Preview'}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() =>
                              downloadDoc.mutate({ docId: document.id, name: niceName })
                            }
                            disabled={downloadDoc.isPending}
                            title={`Download ${niceName}`}
                          >
                            <Download size={14} /> Download
                          </button>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>

                    <td className="ta-right">
                      <div
                        style={{
                          display: 'flex',

                          gap: 8,
                        }}
                      >
                        <label className="btn btn--ghost" style={{ cursor: 'pointer' }}>
                          Upload
                          <input
                            type="file"
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            onChange={handleUploadDoc(type as 'AGREEMENT' | 'RESALE_CERTIFICATE')}
                            disabled={uploadDoc.isPending}
                          />
                        </label>

                        {customer.status !== 'APPROVED' && (
                          <button
                            className="btn btn--danger"
                            onClick={() =>
                              document &&
                              setStatus.mutate({
                                docId: document.id,

                                status: 'REJECTED',
                              })
                            }
                            disabled={!document || isRejected || setStatus.isPending}
                            title={
                              !document
                                ? 'No document uploaded'
                                : isRejected
                                  ? 'Already rejected'
                                  : ''
                            }
                          >
                            Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <h4 style={{ marginTop: 16 }}>Delivery Locations</h4>

          <div className="card card--bleed-top" style={{ padding: 12 }}>
            <div className="muted tiny" style={{ marginBottom: 10 }}>
              Fulfilment methods, default methods, and carrier rates are configured separately for
              each delivery location.
            </div>

            <button type="button" className="btn btn--secondary" onClick={onManageLocations}>
              Manage delivery locations
            </button>
          </div>

          <div className="card card--bleed-top" style={{ padding: 12 }}>
            <NavLink
              to={`/orders?query=${encodeURIComponent(customer.name ?? '')}`}
              className="btn btn--ghost"
              style={{
                display: 'inline-flex',

                alignItems: 'center',

                justifyContent: 'center',

                textDecoration: 'none',
              }}
            >
              View orders for {customer.name || 'this customer'}
            </NavLink>
          </div>

          {missing.length > 0 && (
            <div className="banner banner--warn" style={{ marginTop: 8 }}>
              Missing required: {missing.join(', ')}. Approval is disabled until uploaded.
            </div>
          )}
        </div>

        <div
          className="actions modal__footer"
          style={{
            marginTop: 12,

            gap: 8,
          }}
        >
          {customer.status === 'INVITED' && (
            <button
              className="btn btn--ghost"
              onClick={() => {
                setReinviteEmail(customer.email || '');

                setShowReinviteDialog(true);
              }}
              disabled={invite.isPending}
            >
              Resend invite
            </button>
          )}

          <button
            className="btn btn--primary"
            onClick={onPrimary}
            disabled={
              canEditCustomerDetails
                ? !hasCustomerChanges ||
                  update.isPending ||
                  (usesNotificationEmail && !notificationEmailValid)
                : !canApprove || approve.isPending
            }
            title={
              canEditCustomerDetails
                ? usesNotificationEmail && !notificationEmailValid
                  ? 'Enter a valid notification email'
                  : !hasCustomerChanges
                    ? 'No changes to save'
                    : ''
                : !canApprove
                  ? 'Upload/approve required documents first'
                  : ''
            }
          >
            {canEditCustomerDetails
              ? update.isPending
                ? 'Updating…'
                : 'Update'
              : approve.isPending
                ? 'Approving…'
                : 'Approve'}
          </button>

          {customer.status !== 'ARCHIVED' && (
            <button
              className="btn btn--ghost"
              onClick={onSendOrderDetails}
              disabled={sendOrderDetails.isPending}
              title="Email this customer a summary of their open orders"
            >
              {sendOrderDetails.isPending ? 'Sending…' : 'Send order details'}
            </button>
          )}

          {customer.status !== 'ARCHIVED' ? (
            <button className="btn btn--danger" onClick={onArchive} disabled={archive.isPending}>
              {archive.isPending ? 'Archiving…' : 'Archive'}
            </button>
          ) : (
            <button className="btn" onClick={onUnarchive} disabled={unarchive.isPending}>
              {unarchive.isPending ? 'Restoring…' : 'Unarchive'}
            </button>
          )}
        </div>
      </div>

      {showReinviteDialog && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modal__backdrop" onClick={() => setShowReinviteDialog(false)} />

          <div
            className="modal__panel"
            style={{
              maxWidth: 480,

              width: '100%',
            }}
          >
            <div className="modal__header">
              <h3 className="modal__title">Resend Invite</h3>

              <button
                className="btn btn--ghost"
                onClick={() => setShowReinviteDialog(false)}
                disabled={invite.isPending}
              >
                Close
              </button>
            </div>

            <div className="modal__body">
              <div className="form-control">
                <label>Email</label>

                <input
                  type="email"
                  value={reinviteEmail}
                  onChange={(event) => setReinviteEmail(event.target.value)}
                  placeholder="customer\@example.com"
                  autoFocus
                />

                <div className="muted tiny" style={{ marginTop: 4 }}>
                  Keep the current email or replace it if the original invite was sent to the wrong
                  address.
                </div>
              </div>
            </div>

            <div className="modal__footer">
              <div className="actions" style={{ gap: 8 }}>
                <button
                  className="btn btn--primary"
                  onClick={reinvite}
                  disabled={!reinviteEmail.trim() || invite.isPending}
                >
                  {invite.isPending ? 'Sending…' : 'Send Invite'}
                </button>

                <button
                  className="btn btn--ghost"
                  onClick={() => setShowReinviteDialog(false)}
                  disabled={invite.isPending}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {documentPreview &&
        createPortal(
          <PdfPreviewDialog
            key={documentPreview.url}
            url={documentPreview.url}
            title={`${documentPreview.title} · PDF Preview`}
            onClose={closeDocumentPreview}
            footer={
              <div className="actions customer-document-preview__footer">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() =>
                    downloadDoc.mutate({
                      docId: documentPreview.docId,
                      name: documentPreview.filename,
                    })
                  }
                  disabled={downloadDoc.isPending}
                >
                  <Download size={14} />
                  {downloadDoc.isPending ? 'Downloading…' : 'Download PDF'}
                </button>
                <button type="button" className="btn btn--ghost" onClick={closeDocumentPreview}>
                  Close
                </button>
              </div>
            }
          />,
          document.body
        )}
    </div>
  );
}

// Banner linking to the application-review surface, shown only when applications
// are waiting. Self-contained (own query), so it adds no state to CustomersPage.
function PendingApplicationsBanner() {
  const { data } = usePendingApplications();
  const count = data?.applications?.length ?? 0;
  if (!count) return null;
  return (
    <Link
      to="/customer-applications"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '12px 16px',
        margin: '12px 0',
        borderRadius: 10,
        background: '#fbf0da',
        color: '#9a6200',
        border: '1px solid #f0dcae',
        textDecoration: 'none',
        fontWeight: 600,
      }}
    >
      <span>
        {count} customer application{count > 1 ? 's' : ''} awaiting review
      </span>
      <span>Review →</span>
    </Link>
  );
}
